// Read-back verification for licence expiry — ATC-139.
//
// WHY THIS EXISTS, and it is a specific incident rather than a general
// principle.
//
// `ATC-121` shipped "settable expiry at issuance". The parse block was placed
// inside the `principalClass === 'flightdeck'` branch, so on the DEVICE path —
// every normal environment issuance — it never ran. `expiresAt` was still in
// the server's recognized-field list, so the closed schema ACCEPTED the field
// and returned 200. An operator set an expiry, saw a success, and received a
// perpetual credential. Not one row had ever stored an expiry (Hotfix 054).
//
// Nothing in the console could have caught that, because nothing compared what
// was ASKED FOR against what came BACK. The reveal modal did not show the
// expiry, and the wizard's review screen — whose entire job is to enumerate
// the writes — never mentioned it.
//
// So this module exists to make one class of failure impossible to ship
// silently: a field the server accepts and does not apply. It compares the
// request against the response and says which of those happened. It is pure,
// so it is tested without mounting anything and without a server.
//
// IT IS NOT VALIDATION. `dateInputToIso` validates the input; the server
// refuses a malformed or past date. This runs AFTER a 200, on the question
// those cannot answer: "did the thing I asked for actually take?"

export type ReadBackOutcome =
  /** Nothing was requested, or the stored value matches what was requested. */
  | 'ok'
  /** Requested, and the server reports NOTHING stored. The Hotfix 054 shape. */
  | 'not-applied'
  /** Requested, and the server reports a DIFFERENT value. */
  | 'mismatch'
  /** The server did not report the field at all — an older deployment. */
  | 'unverifiable'

export interface ReadBackResult {
  outcome: ReadBackOutcome
  /** Operator-facing sentence. Empty for `ok`, which needs no words. */
  message: string
}

const OK: ReadBackResult = { outcome: 'ok', message: '' }

/**
 * Compare a requested expiry against the one the server reports storing.
 *
 * `requested` is the value the console put in the request body (`undefined`
 * when the operator asked for a perpetual licence). `returned` is the
 * response's `expiresAt` — `null` for perpetual, `undefined` when the field is
 * absent from the payload entirely.
 *
 * THE `undefined` / `null` DISTINCTION IS THE WHOLE DESIGN. `null` is the
 * server SAYING "perpetual"; `undefined` is the server NOT SAYING. Collapsing
 * them would turn an older deployment into a false alarm, or — far worse — a
 * genuine not-applied into an "we could not tell", which is how this defect
 * stayed invisible in the first place.
 */
export function verifyExpiry(
  requested: string | null | undefined,
  returned: string | null | undefined,
): ReadBackResult {
  if (!requested) {
    // Nothing was asked for. A server that returns a value here would be
    // inventing one, which is not a failure mode worth a branch.
    return OK
  }

  if (returned === undefined) {
    return {
      outcome: 'unverifiable',
      message:
        'The service did not report an expiry on the response, so this console could not confirm it was stored. Open the environment and check the Expires field.',
    }
  }

  if (returned === null || returned === '') {
    return {
      outcome: 'not-applied',
      message:
        'An expiry was requested but the credential was stored as PERPETUAL. It will never expire. Set the expiry from the environment page, and report this — the request was accepted and not applied.',
    }
  }

  // Instant equality, not string equality. The server canonicalises to
  // `yyyy-MM-ddTHH:mm:ss.fffZ`, so `...T23:59:59Z` comes back as
  // `...T23:59:59.000Z` — the same moment, a different string. Comparing
  // strings would make every no-millisecond form a false alarm.
  const asked = Date.parse(requested)
  const got = Date.parse(returned)
  if (!Number.isNaN(asked) && !Number.isNaN(got) && asked === got) {
    return OK
  }

  return {
    outcome: 'mismatch',
    message: `An expiry of ${requested} was requested but the credential was stored with ${returned}. Check the environment before relying on either date.`,
  }
}

/** True when the operator must be shown something. `ok` is silent. */
export function isReadBackProblem(result: ReadBackResult): boolean {
  return result.outcome !== 'ok'
}
